<template>
    <div>
        <form @submit.prevent="importFile">
            <div class="form-group">
                <label>Follow Up Tujuan <span class="text-danger">*</span></label>
                <select v-model.number="selectedFollowUpId" class="form-control" required>
                    <option :value="null">-- Pilih Follow Up --</option>
                    <option v-for="fu in followUps" :key="fu.id" :value="fu.id">
                        {{ fu.name }}
                        <template v-if="fu.is_active != 1"> (Nonaktif)</template>
                        <template v-else-if="fu.expired_at && fu.expired_at < Date.now()"> (Expired)</template>
                    </option>
                </select>
                <small class="form-text text-muted">
                    Kontak dari CSV akan dimasukkan ke follow up ini dan mulai menerima pesan sesuai urutan step.
                </small>
            </div>

            <div class="form-group">
                <label>Upload File CSV:</label>
                <input ref="fileInput" type="file" @change="handleFileChange" accept=".csv" class="form-control" />
                <small class="form-text text-muted">
                    Format CSV harus berisi kolom: <code>name</code>, <code>number</code>.
                    Nomor <code>08xxx</code> akan otomatis diubah jadi <code>628xxx</code>.
                </small>
                <div class="mt-2">
                    <a href="/excel/contohfollowup.csv" download class="btn btn-sm btn-outline-secondary">
                        <i class="fas fa-download"></i> Unduh Contoh CSV
                    </a>
                </div>
            </div>

            <div class="mt-2" v-if="csvParseLoading">
                <div class="alert alert-warning mb-0">
                    Sedang memproses file CSV...
                </div>
            </div>

            <div class="form-group mt-3">
                <label>
                    <input type="checkbox" v-model="updateExisting" />
                    Update / reset progress kontak jika sudah ada di flow ini
                </label>
                <small class="form-text text-muted d-block">
                    Centang jika ingin mereset kontak yang sudah pernah masuk flow — mereka akan mulai lagi dari Step 1.
                </small>
            </div>

            <button type="submit" class="btn btn-primary" :disabled="!csvImportReady || csvParseLoading || !selectedFollowUpId || isImporting">
                <i class="fas fa-file-import"></i>
                {{ isImporting ? 'Meng-import...' : 'Import' }}
            </button>
        </form>

        <div v-if="csvImportReady && parsedRows.length && !importResult" class="mt-4">
            <h5 class="mb-3 fw-600 text-capitalize text-center">Preview Data ({{ parsedRows.length }} baris)</h5>
            <vue-good-table
                :columns="previewColumns"
                :rows="parsedRows"
                :pagination-options="{
                    enabled: true,
                    perPage: 10,
                    perPageDropdown: [10, 25, 50]
                }"
            />
        </div>

        <div v-if="importResult" class="mt-3">
            <div class="alert alert-info">
                <strong>Hasil Import:</strong><br>
                Berhasil: <strong class="text-success">{{ importResult.success_count }}</strong><br>
                Gagal: <strong class="text-danger">{{ importResult.error_count }}</strong>
            </div>
            <div v-if="importResult.errors && importResult.errors.length > 0" class="mt-3">
                <h6>Detail Error:</h6>
                <vue-good-table
                    :columns="errorColumns"
                    :rows="importResult.errors"
                    :pagination-options="{
                        enabled: true,
                        perPage: 10,
                        perPageDropdown: [10, 25, 50]
                    }"
                />
            </div>
            <div class="mt-3">
                <router-link :to="{ name: 'FollowUpContactHistory' }" class="btn btn-sm btn-outline-primary">
                    <i class="fas fa-history"></i> Lihat Contact History
                </router-link>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        initialFollowUpId: {
            type: Number,
            default: null
        }
    },
    data() {
        return {
            followUps: [],
            selectedFollowUpId: this.initialFollowUpId || null,
            selectedFile: null,
            updateExisting: false,
            importResult: null,
            parsedRows: [],
            csvParseLoading: false,
            csvImportReady: false,
            isImporting: false,
            previewColumns: [
                { label: 'Nama', field: 'name' },
                { label: 'Nomor', field: 'number' }
            ],
            errorColumns: [
                { label: 'Baris', field: 'row', width: '80px' },
                { label: 'Nomor', field: 'number' },
                { label: 'Keterangan', field: 'error' }
            ]
        };
    },
    async mounted() {
        await this.loadFollowUps();
    },
    methods: {
        async loadFollowUps() {
            try {
                const response = await this.$store.dispatch("followup/followup_list", { page: 1, limit: 200 });
                this.followUps = response.data.data.follow_ups || [];
            } catch (err) {
                this.$toast.error("Gagal memuat daftar follow up");
            }
        },
        handleFileChange(event) {
            this.selectedFile = event.target.files[0];
            this.importResult = null;
            this.parsedRows = [];
            this.csvImportReady = false;

            if (!this.selectedFile) {
                return;
            }

            const isCsv = this.selectedFile.type === "text/csv" ||
                this.selectedFile.name.toLowerCase().endsWith(".csv");
            if (!isCsv) {
                this.$toast.error("File harus berformat .csv");
                this.selectedFile = null;
                return;
            }

            this.parseCsvPreview(this.selectedFile);
        },
        parseCsvPreview(file) {
            this.csvParseLoading = true;
            this.$papa.parse(file, {
                header: true,
                skipEmptyLines: true,
                complete: (results) => {
                    const rows = results.data || [];
                    this.parsedRows = rows
                        .map(row => this.normalizeRow(row))
                        .filter(row => row.number); // must have at least number
                    this.csvImportReady = this.parsedRows.length > 0;
                    if (!this.csvImportReady) {
                        this.$toast.error("Tidak ada baris valid pada file CSV. Pastikan ada kolom 'number'.");
                    }
                    this.csvParseLoading = false;
                },
                error: () => {
                    this.$toast.error("Gagal membaca file CSV");
                    this.csvParseLoading = false;
                }
            });
        },
        normalizeRow(raw) {
            const row = {};
            Object.keys(raw).forEach(key => {
                if (!key) return;
                row[key.toLowerCase().trim()] = raw[key];
            });

            const name = (row['name'] || row['nama'] || '').toString().trim();
            let number = (row['number'] || row['nomor'] || row['phone'] || row['no'] || row['hp'] || '').toString().trim();

            // Strip non-digit chars
            number = number.replace(/\D/g, '');

            // Normalize 08xxx -> 628xxx (backend also normalizes but we preview nicely)
            if (number.startsWith('0')) {
                number = '62' + number.substring(1);
            }

            return { name: name || number, number };
        },
        async importFile() {
            if (this.csvParseLoading) {
                this.$toast.error("Sedang memproses file CSV");
                return;
            }
            if (!this.selectedFollowUpId) {
                this.$toast.error("Pilih Follow Up tujuan dulu");
                return;
            }
            if (!this.parsedRows.length) {
                this.$toast.error("Tidak ada data valid untuk diimport");
                return;
            }

            this.isImporting = true;
            try {
                const response = await this.$store.dispatch("followup/followup_import_contacts", {
                    id: this.selectedFollowUpId,
                    update_existing: this.updateExisting,
                    contacts: this.parsedRows
                });
                this.importResult = response.data.data;
                this.$toast.success(`Import selesai: ${response.data.data.success_count} berhasil, ${response.data.data.error_count} gagal`);
                this.resetForm();
            } catch (err) {
                const message = err.response?.data?.message || "Gagal import data";
                this.$toast.error("Gagal import: " + message);
            } finally {
                this.isImporting = false;
            }
        },
        resetForm() {
            this.selectedFile = null;
            this.parsedRows = [];
            this.csvImportReady = false;
            if (this.$refs && this.$refs.fileInput) {
                this.$refs.fileInput.value = null;
            }
        }
    }
};
</script>
